import { filterRegistrationFormCourseIds } from './registrationFormDraft';
import { CHECKOUT_CART_BACKUP_SESSION_KEY } from './sessionKeys';
import { isCourseAvailableForPurchase } from './coursePurchaseState';

/**
 * Reads course ids stored in the local cart.
 * @returns {string[]}
 */
export function readCartCourseIds() {
  try {
    const raw = JSON.parse(localStorage.getItem('carrito') || '[]');
    if (!Array.isArray(raw)) {
      return [];
    }
    return raw
      .map((item) => (item && typeof item._id === 'string' ? item._id.trim() : ''))
      .filter(Boolean);
  } catch {
    return [];
  }
}

export function readCartCourses() {
  try {
    const raw = JSON.parse(localStorage.getItem('carrito') || '[]');
    return Array.isArray(raw) ? raw : [];
  } catch {
    return [];
  }
}

export function backupCheckoutCartCourses(courses) {
  if (!Array.isArray(courses) || courses.length === 0) {
    return;
  }
  try {
    sessionStorage.setItem(CHECKOUT_CART_BACKUP_SESSION_KEY, JSON.stringify(courses));
  } catch {
    /* ignore storage failures */
  }
}

export function readCheckoutCartBackup() {
  try {
    const raw = JSON.parse(sessionStorage.getItem(CHECKOUT_CART_BACKUP_SESSION_KEY) || '[]');
    return Array.isArray(raw) ? raw : [];
  } catch {
    return [];
  }
}

export function restoreCheckoutCartBackup() {
  const backup = readCheckoutCartBackup();
  if (backup.length > 0) {
    localStorage.setItem('carrito', JSON.stringify(backup));
  }
  return backup;
}

export function clearCheckoutCartBackup() {
  try {
    sessionStorage.removeItem(CHECKOUT_CART_BACKUP_SESSION_KEY);
  } catch {
    /* ignore storage failures */
  }
}

export function upsertCartCourse(course) {
  const cart = readCartCourses();
  if (!course || typeof course._id !== 'string' || !isCourseAvailableForPurchase(course)) {
    return cart;
  }

  const courseId = course._id;
  const nextCart = cart.some((item) => item?._id === courseId)
    ? cart.map((item) => (item?._id === courseId ? course : item))
    : [...cart, course];

  localStorage.setItem('carrito', JSON.stringify(nextCart));
  return nextCart;
}

export function reconcileCartWithPublicCourses(cartCourses, currentCourses, options = {}) {
  if (!Array.isArray(cartCourses)) return [];
  const currentById = new Map(
    (Array.isArray(currentCourses) ? currentCourses : [])
      .map((course) => ({ course, id: typeof course?._id === 'string' ? course._id.trim() : '' }))
      .filter(({ course, id }) => id && isCourseAvailableForPurchase(course))
      .map(({ course, id }) => [id, course._id === id ? course : { ...course, _id: id }]),
  );
  const verifiedIds = new Set(
    (Array.isArray(options.verifiedCourseIds) ? options.verifiedCourseIds : [])
      .map((id) => (typeof id === 'string' ? id.trim() : ''))
      .filter(Boolean),
  );
  const unavailableIds = new Set(
    (Array.isArray(options.unavailableCourseIds) ? options.unavailableCourseIds : [])
      .map((id) => (typeof id === 'string' ? id.trim() : ''))
      .filter(Boolean),
  );
  return cartCourses
    .map((course) => {
      const id = typeof course?._id === 'string' ? course._id.trim() : '';
      if (!id) return null;
      if (currentById.has(id)) return currentById.get(id);
      if (unavailableIds.has(id) || verifiedIds.has(id)) return null;
      return options.preserveUnverified === true ? course : null;
    })
    .filter(Boolean);
}

export function mergeCartCoursesById(cartCourses, currentCourses) {
  if (!Array.isArray(cartCourses)) {
    return [];
  }

  const currentCoursesById = new Map(
    (Array.isArray(currentCourses) ? currentCourses : [])
      .filter((course) => course && typeof course._id === 'string')
      .map((course) => [course._id, course]),
  );

  return cartCourses.map((course) => {
    const courseId = course && typeof course._id === 'string' ? course._id : '';
    return currentCoursesById.get(courseId) || course;
  });
}

/**
 * Merges registration draft course ids with cart course ids.
 * @param {string[]} draftCourseIds
 * @param {string[]} cartCourseIds
 * @param {string[]} availableCourseIds
 * @returns {string[]}
 */
export function mergeRegistrationCoursesWithCart(
  draftCourseIds,
  cartCourseIds,
  availableCourseIds,
) {
  const draftFiltered = filterRegistrationFormCourseIds(draftCourseIds, availableCourseIds);
  const cartFiltered = filterRegistrationFormCourseIds(cartCourseIds, availableCourseIds);
  return [...new Set([...draftFiltered, ...cartFiltered])];
}
